// src/utils/memberAuthLink.js
//
// Pass 55 Stage D — the CANONICAL, fail-closed operation that links an EXISTING organizationMember to
// a Firebase Auth UID and backfills that member's assigned bookings. It preserves the identity
// invariant:
//   organizationMember.userId    = permanent Motus business identity (NEVER overwritten)
//   organizationMember.authUserId = Firebase Auth UID (login / delivery)
// Reuses the Pass-51 instructorAuthBackfill for the booking side (no second mechanism). Pure — the
// caller supplies the existing member, the Auth UID, and the member's bookings.

import { planInstructorAuthBackfill } from './instructorAuthBackfill';
import { identityEmailsMatch } from './identityEmail';

// Fail-closed validation. `existingMembers` = all members in the org (to detect cross-links).
export function validateMemberAuthLink({ member, authUserId, actorEmail = '', organizationId = '', existingMembers = [] } = {}) {
  const blockers = [];
  if (!member || !member.userId) blockers.push('target_member_missing');
  if (member && organizationId && member.organizationId && member.organizationId !== organizationId) blockers.push('wrong_organization');
  if (!authUserId) blockers.push('no_auth_uid');
  // the Auth UID must never become the business identity
  if (member && authUserId && member.userId === authUserId) blockers.push('auth_uid_equals_business_userid');
  // member already linked to a DIFFERENT Auth UID
  if (member && member.authUserId && authUserId && member.authUserId !== authUserId) blockers.push('member_already_linked_other_uid');
  // this Auth UID already linked to a DIFFERENT member in the same org
  if (member && authUserId && existingMembers.some((m) => m.authUserId === authUserId && m.userId !== member.userId && m.organizationId === member.organizationId)) blockers.push('auth_uid_linked_to_other_member');
  // invite email must match the member's email when both are known. Pass 88.4 — compare under the narrow
  // identity canonicalization (gmail.com ≡ googlemail.com) so the Google alias is not a false mismatch.
  if (actorEmail && member && member.email && !identityEmailsMatch(actorEmail, member.email)) blockers.push('email_mismatch');
  return {
    ok: blockers.length === 0,
    blockers,
    idempotent: !!(member && member.authUserId && member.authUserId === authUserId), // re-accept = safe no-op (+ may repair backfill)
  };
}

// The controlled plan: a member patch that sets ONLY authUserId (userId + all other fields untouched),
// plus the booking backfill (instructorAuthUserId only) from the canonical Pass-51 planner.
export function planMemberAuthLink(member, authUserId, bookings = []) {
  const memberPatch = { authUserId }; // NEVER includes userId
  const { updates } = planInstructorAuthBackfill({ ...member, authUserId }, bookings);
  return { memberPatch, bookingUpdates: updates };
}
